/**
 * Where each Minecraft launcher keeps the game jar and the worlds, per operating system.
 * Used by the setup wizard (web) to show exact, copyable paths instead of generic help.
 * Same folders as the desktop scanner (apps/desktop/src-tauri/src/scan.rs).
 */

export type Launcher = "official" | "modrinth" | "curseforge" | "prism" | "other";
export type OS = "windows" | "mac" | "linux";

export interface LauncherInfo {
  id: Launcher;
  name: string;
  /** one line under the name on the launcher tile */
  hint: string;
}

export const LAUNCHERS: LauncherInfo[] = [
  { id: "official", name: "Official launcher", hint: "Minecraft Launcher by Mojang" },
  { id: "modrinth", name: "Modrinth App", hint: "Modpacks & profiles" },
  { id: "curseforge", name: "CurseForge", hint: "CurseForge app instances" },
  { id: "prism", name: "Prism / MultiMC", hint: "Prism Launcher, MultiMC" },
  { id: "other", name: "Something else", hint: "Server, another launcher…" },
];

export const OS_NAME: Record<OS, string> = { windows: "Windows", mac: "macOS", linux: "Linux" };

export function detectOS(): OS {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const p = `${nav.userAgentData?.platform ?? ""} ${navigator.platform ?? ""} ${navigator.userAgent}`.toLowerCase();
  if (p.includes("mac") || p.includes("iphone") || p.includes("ipad")) return "mac";
  if (p.includes("win")) return "windows";
  return "linux";
}

/** base folder of a launcher (where versions/, saves/, profiles/… live) */
function base(l: Launcher, os: OS): string {
  const win = os === "windows";
  const mac = os === "mac";
  switch (l) {
    case "modrinth": return win ? "%APPDATA%\\ModrinthApp" : mac ? "~/Library/Application Support/ModrinthApp" : "~/.local/share/ModrinthApp";
    case "curseforge": return win ? "%USERPROFILE%\\curseforge\\minecraft" : mac ? "~/Documents/curseforge/minecraft" : "~/curseforge/minecraft";
    case "prism": return win ? "%APPDATA%\\PrismLauncher" : mac ? "~/Library/Application Support/PrismLauncher" : "~/.local/share/PrismLauncher";
    default: return win ? "%APPDATA%\\.minecraft" : mac ? "~/Library/Application Support/minecraft" : "~/.minecraft";
  }
}

function join(os: OS, ...parts: string[]) {
  return parts.join(os === "windows" ? "\\" : "/");
}

export interface PathHelp {
  /** folder to open (copyable) */
  folder: string;
  /** what to look for inside it */
  find: string;
}

/** Where the client jar is. */
export function jarHelp(l: Launcher, os: OS): PathHelp {
  const b = base(l, os);
  switch (l) {
    case "modrinth": return { folder: join(os, b, "meta", "versions"), find: "Open the newest version folder (e.g. 26.2) and take the .jar with the same name." };
    case "curseforge": return { folder: join(os, b, "Install", "versions"), find: "Open the newest version folder and take the .jar with the same name." };
    case "prism": return { folder: join(os, b, "libraries", "com", "mojang", "minecraft"), find: "Open the newest version folder and take minecraft-…-client.jar." };
    default: return { folder: join(os, b, "versions"), find: "Open the newest version folder (e.g. 26.2) and take 26.2.jar." };
  }
}

/** Where the worlds are. */
export function savesHelp(l: Launcher, os: OS): PathHelp {
  const b = base(l, os);
  switch (l) {
    case "modrinth": return { folder: join(os, b, "profiles"), find: "Open your profile, then saves — every folder in there is a world." };
    case "curseforge": return { folder: join(os, b, "Instances"), find: "Open your instance, then saves — every folder in there is a world." };
    case "prism": return { folder: join(os, b, "instances"), find: "Open your instance, then .minecraft (or minecraft), then saves." };
    default: return { folder: join(os, b, "saves"), find: "Every folder in here is a world." };
  }
}

/** Three short steps to get to a folder and drag a file out of it. */
export function openFolderSteps(os: OS): [string, string] {
  if (os === "mac") return ["In Finder press ⇧⌘G (Go to Folder)", "Paste the path and press Return"];
  if (os === "linux") return ["Open your file manager and press Ctrl+L", "Paste the path and press Enter"];
  return ["Press Win+E to open File Explorer", "Click the address bar, paste, press Enter"];
}

export function zipHelp(os: OS): string {
  if (os === "mac") return "Right-click the world folder, then Compress.";
  if (os === "linux") return "Right-click the world folder, then Compress….";
  return "Right-click the world folder, then Compress to ZIP file (Windows 10: Send to, then Compressed folder).";
}
